import React, { useState } from "react";
import Text from "./Text";
import BaseButton from "./BaseButton";
import { RoutingMode } from "@/features/pathFinding/types/routing";
import { trackRouteFeedback } from "@/utils/analytics";
import { QRCodeSVG } from "qrcode.react";
import Skeleton from "./Skeleton";
import { useTranslation } from "react-i18next";

import {
  MdOutlineArrowBackIos,
  MdOutlineElevator,
  MdOutlineStairs,
  MdOutlineThumbUp,
  MdOutlineThumbDown,
  MdOutlineHome,
} from "react-icons/md";

type SidebarRouteNavigationProps = {
  floorName: string;
  roomLabel: string;
  faculty_name?: string;
  onBack?: () => void;
  onGoHome?: () => void; // ホームへ戻る
  onViewDefaultRoute?: () => void;
  onViewBarrierfreeRoute?: () => void;
  currentRoutingMode?: RoutingMode;
  isElevatorButtonVisible?: boolean;
  imageUrl?: string;
  className?: string;
  shareUrl?: string | null;
  isMobile?: boolean;
  isKioskMode?: boolean;
  onHeightChange?: (height: number) => void;
  isShareUrlLoading?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
};

const SidebarRouteNavigation: React.FC<SidebarRouteNavigationProps> = ({
  floorName,
  roomLabel,
  faculty_name,
  onBack,
  onGoHome,
  onViewDefaultRoute,
  onViewBarrierfreeRoute,
  currentRoutingMode = "default",
  isElevatorButtonVisible = true,
  className = "",
  shareUrl = null,
  isMobile = false,
  isKioskMode = false,
  isShareUrlLoading = false,
  containerRef,
}) => {
  // 翻訳インスタンスのインポート
  const { t } = useTranslation();

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const markFeedbackAsSubmitted = () => {
    setFeedbackSubmitted(true);
  };

  const baseClasses = [
    "bg-white",
    "box-border",
    "flex",
    "flex-col",
    isMobile ? "gap-4" : "gap-6",
    "items-start",
    "p-4",
    "sm:p-10",
    "relative",
    "w-full",
    "h-auto",
    "max-h-full",
    "sm:h-full",
    "overflow-y-auto",
    "shrink-0",
  ].join(" ");

  const handleFeedbackClick = (isHelpful: boolean) => () => {
    trackRouteFeedback(isHelpful);
  };

  return (
    <div ref={containerRef} className={`${baseClasses} ${className}`}>
      {/* 戻る / ホーム ボタン */}
      {(onBack || onGoHome) && (
        <div className="flex items-center justify-between w-full relative shrink-0 mb-2">
          {onBack && (
            <BaseButton onClick={onBack} icon={MdOutlineArrowBackIos}>
              {t("戻る")}
            </BaseButton>
          )}
          {onGoHome && (
            <BaseButton onClick={onGoHome} icon={MdOutlineHome} textVariant="body/s">
              {t("最初の画面に戻る")}
            </BaseButton>
          )}
        </div>
      )}

      {/* コンテンツ */}
      <div className={`flex flex-col items-start relative shrink-0 w-full gap-4`}>
        {/* タイムライン + 施設情報 */}
        <div className={`flex items-stretch w-full ${isMobile ? "gap-3" : "gap-4"}`}>
          {/* タイムラインのカラム */}
          <div className="flex flex-col items-center shrink-0 w-6">
            {/* 上のドット */}
            <div className="size-6 rounded-full bg-AccentBlue shrink-0" />
            {/* 線 */}
            <div className="w-1 min-h-[48px] bg-Border my-2 flex-1" />
            {/* 下のドット */}
            <div className="size-6 rounded-full bg-MiraiPrimary shrink-0" />
          </div>

          {/* 施設情報 (残り幅に収める。w-full だとタイムライン列+gap分だけ親からはみ出す) */}
          <div
            className={`flex flex-col items-start not-italic relative min-w-0 flex-1 ${
              isMobile ? "gap-4" : "gap-8"
            }`}
          >
            <div className="flex flex-col items-start relative shrink-0 w-full">
              <Text as="p" variant="body/m" weight="regular">
                {t("本部棟 3階")}
              </Text>
              <Text as="p" variant="heading/s" weight="bold">
                {t("正面玄関前 (現在地)")}
              </Text>
            </div>
            <div className="flex flex-col items-start relative shrink-0 w-full">
              <Text as="p" variant="body/m" weight="regular">
                {t(floorName)}
              </Text>
              <Text as="p" variant="heading/m" weight="bold">
                {roomLabel}
              </Text>
              {faculty_name && (
                <Text as="p" variant="body/s" weight="regular">
                  {faculty_name}
                </Text>
              )}
            </div>
          </div>
        </div>

        {/* 経路探索切り替え */}
        <div className={`flex flex-col items-start relative shrink-0 w-full gap-2`}>
          {currentRoutingMode === "default" &&
            isElevatorButtonVisible &&
            onViewBarrierfreeRoute && (
              <BaseButton
                icon={MdOutlineElevator}
                onClick={onViewBarrierfreeRoute}
                isSelected={false}
                className="w-full"
              >
                {t("エレベーターを使う経路に切り替える")}
              </BaseButton>
            )}
          {currentRoutingMode === "barrier-free" && onViewDefaultRoute && (
            <BaseButton
              icon={MdOutlineStairs}
              onClick={onViewDefaultRoute}
              isSelected={false}
              className="w-full"
            >
              {t("階段を使う経路に切り替える")}
            </BaseButton>
          )}
        </div>

        {(isKioskMode || !isMobile) && (
          <div className="flex flex-col items-start w-full gap-2">
            <Text as="h2" variant="heading/s" weight="bold">
              {t("スマホから経路を見る")}
            </Text>
            <Text as="p" variant="body/m" weight="regular" className="">
              {t(
                "二次元コードを読み取ると、お手持ちのスマートフォンからもこの経路を引き続き確認できます。",
              )}
            </Text>
            <div className="w-full flex flex-col items-center justify-center gap-3 py-4">
              {isShareUrlLoading ? (
                <>
                  <div className="p-4 bg-Background border border-Border rounded-2xl">
                    <Skeleton className="h-[196px] w-[196px]" />
                  </div>
                  <Skeleton className="h-4 w-48 max-w-[280px]" />
                </>
              ) : shareUrl ? (
                <>
                  <div className="p-4 bg-Background border border-Border rounded-2xl">
                    <QRCodeSVG
                      value={shareUrl}
                      size={196}
                      level="M"
                      includeMargin
                      title={t("経路共有用の二次元コード")}
                    />
                  </div>
                  <p className="text-xs text-TextSecondary text-center break-all px-16">
                    {shareUrl}
                  </p>
                </>
              ) : (
                <div className="w-40 h-40 bg-[#f5f4f2] border border-Border rounded-2xl flex items-center justify-center px-4 text-center">
                  <Text as="span" variant="body/s" weight="regular">
                    {t("共有用の経路URLを生成できませんでした。")}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}

        {/* フィードバック */}
        <div className="flex flex-col gap-2 items-start w-full">
          <Text as="p" variant="body/s" weight="regular">
            {t("役に立ちましたか？")}
          </Text>
          {feedbackSubmitted ? (
            <div>
              <Text as="h3" variant="body/m" weight="regular" className="text-Black pt-3">
                {t("フィードバックありがとうございます！")}
              </Text>
              <div className="sm:hidden">
                <Text as="h3" variant="body/m" weight="regular" className="text-Black pt-3">
                  {t("よろしければ、")}
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSd7E__fb3gPGDHCEKTl8tv6SPtv2XB7CvxBSm1tBMQ0lV6Rhg/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {t("アンケート")}
                  </a>
                  {t("にもご協力ください！")}
                </Text>
                {/* スマホで見切れないように下の余白は残しておくのがおすすめ */}
                <div className="h-32" />
              </div>
            </div>
          ) : (
            <div className="flex gap-4 w-full">
              <BaseButton
                icon={MdOutlineThumbUp}
                isSelected={false}
                className="flex-1"
                onClick={() => {
                  handleFeedbackClick(true);
                  markFeedbackAsSubmitted();
                }}
              >
                {t("役に立った")}
              </BaseButton>
              <BaseButton
                icon={MdOutlineThumbDown}
                isSelected={false}
                className="flex-1"
                onClick={() => {
                  handleFeedbackClick(false);
                  markFeedbackAsSubmitted();
                }}
              >
                {t("役に立たなかった")}
              </BaseButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SidebarRouteNavigation;
